import {
  FILTER_FIELD_BY_KEY,
  FILTER_FIELDS,
  FilterCondition,
  FilterFieldKey,
  QueryContext,
  fieldByToken,
  formatNumber,
  quoteValue,
} from '@app/model';
import { RecentSearch } from '@app/core';

/** A search the user starts with one click, e.g. "Not reviewed". */
export interface QueryPreset {
  /** i18n key of the label. */
  label: string;
  query: string;
}

/** The presets of the concept; `reviewer:me` needs the user's name. */
export const DEFAULT_PRESETS: QueryPreset[] = [
  { label: 'filter.preset.unreviewed', query: 'status:unreviewed' },
  { label: 'filter.preset.mine', query: 'reviewer:me' },
  { label: 'filter.preset.score70', query: 'score:>=70' },
  { label: 'filter.preset.funded2024', query: 'funded:>=2024' },
];

/**
 * A line of the suggestions: `insert` puts text into the field (a prefix),
 * `commit` adds the query as chips, `preset` likewise with a label, `recent`
 * opens a search of before.
 */
export type QuerySuggestion =
  | { kind: 'insert'; text: string; field: FilterFieldKey; value: string }
  | { kind: 'commit'; query: string; field: FilterFieldKey; value: string }
  | { kind: 'preset'; query: string; label: string }
  | { kind: 'recent'; search: RecentSearch };

const MAX_VALUES = 8;

const contains = (text: string, part: string) =>
  text.toLowerCase().includes(part.toLowerCase());

/** The suggestions for the text in the field of the query bar. */
export const querySuggestions = (
  text: string,
  context: QueryContext,
  presets: QueryPreset[],
  recent: RecentSearch[]
): QuerySuggestion[] => {
  const fields = FILTER_FIELDS.filter((x) => !x.hidden && x.key !== 'name');
  const trimmed = text.trim();
  if (!trimmed) {
    return [
      ...presets.map((x): QuerySuggestion => ({
        kind: 'preset',
        query: x.query,
        label: x.label,
      })),
      ...recent.map((search): QuerySuggestion => ({ kind: 'recent', search })),
      ...fields.map((x): QuerySuggestion => ({
        kind: 'insert',
        text: `${x.token}:`,
        field: x.key,
        value: `${x.token}:`,
      })),
    ];
  }
  const token = /^([A-Za-z_]+):(.*)$/s.exec(trimmed);
  if (token) {
    const field = fieldByToken(token[1]);
    if (!field) {
      return [];
    }
    const prefix = `${field.token}:`;
    const lastComma = token[2].lastIndexOf(',');
    const done = lastComma >= 0 ? token[2].slice(0, lastComma + 1) : '';
    const part = token[2].slice(lastComma + 1).replace(/^"/, '');
    const commit = (value: string, shown = value): QuerySuggestion => ({
      kind: 'commit',
      query: `${prefix}${done}${quoteValue(value)}`,
      field: field.key,
      value: shown,
    });
    if (field.key === 'sectors') {
      return (context.sectors ?? [])
        .filter((x) => contains(x.name, part))
        .slice(0, MAX_VALUES)
        .map((x) => commit(x.name));
    }
    if (field.key === 'country_code') {
      return (context.countries ?? [])
        .filter((x) => x.toLowerCase().startsWith(part.toLowerCase()))
        .slice(0, MAX_VALUES)
        .map((x) => commit(x));
    }
    if (field.kind === 'enum') {
      const aliases = Object.entries(field.valueAliases ?? {});
      return (field.options ?? [])
        .filter((x) => contains(x, part))
        .map((option) => {
          const alias = aliases.find(([, x]) => x === option)?.[0];
          return {
            kind: 'commit' as const,
            query: `${prefix}${alias ?? quoteValue(option)}`,
            field: field.key,
            value: option,
          };
        });
    }
    if (field.kind.endsWith('-range')) {
      const examples: Record<string, string[]> = {
        'number-range': ['50..500', '>=50', '<=500'],
        'money-range': ['10m..100m', '>=500k', '<=1bn'],
        'year-range': ['2015..2020', '>=2015', '<=2020'],
        'date-range': ['2015..2020', '>=2015-06-01', '<=2020'],
      };
      return part
        ? [commit(part)]
        : examples[field.kind].map((x) => ({
            kind: 'insert' as const,
            text: `${prefix}${x}`,
            field: field.key,
            value: x,
          }));
    }
    return part ? [commit(part)] : [];
  }
  return [
    {
      kind: 'commit',
      query: quoteValue(trimmed),
      field: 'name',
      value: trimmed,
    },
    ...(context.sectors ?? [])
      .filter((x) => contains(x.name, trimmed))
      .slice(0, 5)
      .map((x): QuerySuggestion => ({
        kind: 'commit',
        query: `sector:${quoteValue(x.name)}`,
        field: 'sectors',
        value: x.name,
      })),
    ...(context.countries ?? [])
      .filter((x) => x.toLowerCase() === trimmed.toLowerCase())
      .map((x): QuerySuggestion => ({
        kind: 'commit',
        query: `country:${x}`,
        field: 'country_code',
        value: x,
      })),
    {
      kind: 'commit',
      query: `keyword:${quoteValue(trimmed)}`,
      field: 'keywords',
      value: trimmed,
    },
    ...fields
      .filter((x) => x.token.startsWith(trimmed.toLowerCase()))
      .map((x): QuerySuggestion => ({
        kind: 'insert',
        text: `${x.token}:`,
        field: x.key,
        value: `${x.token}:`,
      })),
  ];
};

/** The value of a chip, e.g. "Payments, Lending" or "50 – 500". */
export const chipValue = (
  condition: FilterCondition,
  context: QueryContext
): string => {
  const field = FILTER_FIELD_BY_KEY.get(condition.field);
  const value = condition.value;
  switch (value.kind) {
    case 'raw': {
      const [, operator, raw] = value.condition as [string, string, unknown];
      const text = Array.isArray(raw) ? raw.join(', ') : String(raw);
      // Operators that read as "is one of" need no word.
      return ['anyof', 'in', '=', 'contains'].includes(operator)
        ? text
        : `${operator} ${text}`;
    }
    case 'text':
      return value.value;
    case 'multi':
    case 'enum':
      if (condition.field === 'business_id') {
        return String(value.values.length);
      }
      return value.values
        .map((x) =>
          condition.field === 'sectors'
            ? ((context.sectors ?? []).find((s) => s.id === x)?.name ??
              String(x))
            : String(x)
        )
        .join(', ');
    case 'range': {
      const bound = (x: number | string | null) =>
        typeof x === 'number'
          ? field?.kind === 'money-range'
            ? formatNumber(x, true).toUpperCase().replace('BN', ' bn')
            : field?.kind === 'year-range' || field?.kind === 'date-range'
              ? String(x)
              : x.toLocaleString('en-US')
          : (x ?? '');
      if (value.from !== null && value.from === value.to) {
        return bound(value.from);
      }
      if (value.to === null) {
        return `≥ ${bound(value.from)}`;
      }
      if (value.from === null) {
        return `≤ ${bound(value.to)}`;
      }
      return `${bound(value.from)} – ${bound(value.to)}`;
    }
  }
};
